import React, { useState, useEffect } from 'react';

/**
 * Top Loading Bar - Shows progress bar at top of page during navigation
 * Usage:
 *   import { useTopLoader } from '../components/TopLoadingBar';
 *   const { start, done } = useTopLoader();
 *   start(); // Start loading
 *   done();  // Complete loading
 */

// Global state for the loading bar
let listeners = [];
let isLoading = false;
let progress = 0;

const notify = () => listeners.forEach(l => l({ isLoading, progress }));

export const topLoader = {
  start: () => {
    isLoading = true;
    progress = 10;
    notify();
    // Auto-increment progress
    const interval = setInterval(() => {
      if (!isLoading) {
        clearInterval(interval);
        return;
      }
      // Slow down as it gets closer to 90%
      progress = Math.min(90, progress + (90 - progress) * 0.1);
      notify();
    }, 200);
  },
  done: () => {
    progress = 100;
    notify();
    setTimeout(() => {
      isLoading = false;
      progress = 0;
      notify();
    }, 300);
  },
  set: (value) => {
    progress = value;
    notify();
  }
};

export const useTopLoader = () => {
  return { start: topLoader.start, done: topLoader.done, set: topLoader.set };
};

export const TopLoadingBar = ({ height = 3 }) => {
  const [state, setState] = useState({ isLoading: false, progress: 0 });

  useEffect(() => {
    const handler = (s) => setState({ ...s });
    listeners.push(handler);
    return () => {
      listeners = listeners.filter(l => l !== handler);
    };
  }, []);

  if (!state.isLoading && state.progress === 0) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height,
        zIndex: 9999,
        pointerEvents: 'none',
      }}
    >
      <div
        style={{
          height: '100%',
          width: `${state.progress}%`,
          background: 'linear-gradient(90deg, #facc15, #f97316, #ef4444)',
          transition: state.progress === 100
            ? 'width 0.2s ease-out, opacity 0.3s ease-out'
            : 'width 0.2s ease-out',
          opacity: state.progress === 100 ? 0 : 1,
          boxShadow: '0 0 10px #f97316, 0 0 5px #ef4444',
        }}
      />
    </div>
  );
};

export default TopLoadingBar;
